import { REGIONS, COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';
import { API_SOURCES } from './pressroomFeed';

export interface Props {
  region?: 'US' | 'CN';
  apiSource?: string;
  apiUrlOverride?: string;
  defaultThumbnail?: string;
  initialFilterYear?: string;
  heroSection?: {
    title?: string;
    titleColor?: {
      md?: string;
      _?: string;
    };
    backgroundImage?: {
      md?: string;
      _?: string;
    };
  };
  appDownload?: {
    title?: string;
    subtitle?: string;
    iosDownloadText?: string;
    androidDownloadText?: string;
    iOSStoreLink?: string;
    iOSAppLink?: string;
    androidStoreLink?: string;
    androidAppLink?: string;
    androidApkLink?: string;
  };
  pressroomLink?: {
    url?: string;
    text?: string;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'region',
    label: 'Region',
    type: types.ENUM,
    options: REGIONS
  },
  {
    key: 'apiSource',
    label: 'API Source',
    tooltip: 'Where the posts come from. "Press Room" is the ff.com press room API. "FF App Feed" reads the FF app\'s post list for the Region (https://app-us.ff.com/api/v2/user/live2 for US, https://app-cn.faradayfuturecn.com/api/v2/user/live2 for CN, unless the API URL Override names another one in the same format); its posts are loaded once and searched and filtered by year in the browser.',
    type: types.ENUM,
    options: Object.values(API_SOURCES)
  },
  {
    key: 'apiUrlOverride',
    label: 'API URL Override',
    tooltip: 'Absolute URL, a path, or a query string only (e.g. "?tag=a"), read in the format of the API Source above. A path is appended to the region API domain, and any query params given here replace the ones the widget builds. A "size" param sets how many posts one page holds (default 5) and each Load More adds that many. An "author_id" param (e.g. "author_id=178", or several comma separated) shows only those authors\' posts, for both API sources.',
    type: types.TEXT,
  },
  {
    key: 'defaultThumbnail',
    label: 'Default Thumbnail',
    type: types.S3_ASSET_URL,
  },
  {
    key: 'initialFilterYear',
    label: 'The starting Year of the filter',
    type: types.TEXT,
  },
  {
    key: 'heroSection',
    label: 'Hero Section',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'titleColor',
        label: 'Title Color',
        type: types.OBJECT,
        children: [
          {
            key: '_',
            label: 'Mobile (or Base)',
            type: types.COLOR_PICKER,
          },
          {
            key: 'md',
            label: 'Desktop',
            type: types.COLOR_PICKER,
          },
        ],
      },
      {
        key: 'backgroundImage',
        label: 'Background Image',
        type: types.OBJECT,
        children: [
          {
            key: '_',
            label: 'Mobile (or Base)',
            type: types.S3_ASSET_URL,
          },
          {
            key: 'md',
            label: 'Desktop',
            type: types.S3_ASSET_URL,
          },
        ],
      },
    ]
  },
  {
    key: 'appDownload',
    label: 'App Download',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TEXT,
      },
      {
        key: 'subtitle',
        label: 'Subtitle',
        type: types.TEXT,
      },
      {
        key: 'iosDownloadText',
        label: 'iOS Download Text',
        type: types.TEXT,
      },
      {
        key: 'androidDownloadText',
        label: 'Android Download Text',
        type: types.TEXT,
      },
      {
        key: 'iOSStoreLink',
        label: 'iOS Store link',
        type: types.TEXT,
      },
      {
        key: 'iOSAppLink',
        label: 'iOS App link (Deep Link)',
        type: types.TEXT,
      },
      {
        key: 'androidStoreLink',
        label: 'Android Store Link',
        type: types.TEXT,
      },
      {
        key: 'androidAppLink',
        label: 'Android App Link (Deep Link)',
        type: types.TEXT,
      },
      {
        key: 'androidApkLink',
        label: 'Android Apk Link',
        type: types.TEXT,
      },
    ],
  },
  {
    key: 'pressroomLink',
    label: 'Pressroom Link',
    type: types.OBJECT,
    children: [
      {
        key: 'url',
        label: 'URL',
        type: types.TEXT,
      },
      {
        key: 'text',
        label: 'Text',
        type: types.TEXT,
      },
    ]
  },
];

const defaultProps: Props = {
  region: 'US',
  apiSource: API_SOURCES.PRESS_ROOM,
  apiUrlOverride: '',
  defaultThumbnail: 'https://genesis-cdn.ff.com/press_room/2023-09/0927/0928-PCPC-default.jpg',
  initialFilterYear: '2021',
  heroSection: {
    title: 'Press Room',
    titleColor: {
      _: '#000000',
      md: '#000000',
    },
    backgroundImage: {
      _: 'https://genesis-cdn.ff.com/press_room/2023-09/0927/0928-PCPC-default.jpg',
      md: 'https://genesis-cdn.ff.com/press_room/2023-09/0927/0928-PCPC-default.jpg',
    },
  },
  appDownload: {
    title: 'FF Intelligent App',
    subtitle: 'The Ultimate Intelligent TechLuxury',
    iosDownloadText: 'iOS Download',
    androidDownloadText: 'Android Download',
    iOSStoreLink: 'https://apps.apple.com/us/app/faraday-future/id1600637235?ls=1',
    iOSAppLink: 'https://faradayfuture2023.app.link/YajHk0j7Nxb',
    androidStoreLink: 'https://play.google.com/store/apps/details?id=com.faradayfuture.online.foreign',
    androidAppLink: 'faradayfuture://app-us',
    androidApkLink: 'https://www.faradayfuturecn.com/cn/apk/latest.apk',
  },
  pressroomLink: {
    url: 'https://www.ff.com/us/press-room/',
    text: 'News before 2021',
  },
};

export default {
  title: 'Pressroom List',
  id: 'PressroomList',
  propsSchema,
  defaultProps,
}
